import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Input, Row, Select, Space, Switch, Tag, Typography, message } from 'antd';
import { StopOutlined, SaveOutlined } from '@ant-design/icons';
import Flex from 'components/shared-components/Flex';
import apiClient from 'services/apiClient';
import { useMyContext } from 'Context/MyContextProvider';
import { ROW_GUTTER } from 'constants/ThemeConstant';

const { Text } = Typography;

const MATCH_MODE_OPTIONS = [
  { value: 'contains', label: 'Contains' },
  { value: 'word', label: 'Whole word' },
  { value: 'exact', label: 'Exact text' },
];

const parseKeywords = (value) => {
  return (value || '')
    .split('\n')
    .map((v) => v.trim())
    .filter(Boolean);
};

const KeywordRestriction = () => {
  const { api, authToken } = useMyContext();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`${api}keyword-restriction`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const row = res?.data?.data || {};
      form.setFieldsValue({
        enabled: row.enabled === true || row.enabled === 1,
        match_mode: row.match_mode || 'contains',
        store_as: row.store_as || '',
        reply_message: row.reply_message || '',
        keywords_text: Array.isArray(row.keywords) ? row.keywords.join('\n') : '',
      });
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to load keyword restriction');
    } finally {
      setLoading(false);
    }
  }, [api, authToken, form]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        enabled: values.enabled === true,
        match_mode: values.match_mode,
        store_as: values.store_as?.trim() || null,
        reply_message: values.reply_message?.trim() || null,
        keywords: parseKeywords(values.keywords_text),
      };
      setSaving(true);
      await apiClient.post(`${api}keyword-restriction`, payload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      message.success('Keyword restriction updated');
      loadConfig();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Failed to save keyword restriction');
    } finally {
      setSaving(false);
    }
  };

  const previewKeywords = parseKeywords(form.getFieldValue('keywords_text')).slice(0, 12);

  return (
    <Card
      loading={loading}
      title={(
        <Flex alignItems="center" style={{ gap: 8 }}>
          <StopOutlined />
          <span>Global Keyword Restriction</span>
        </Flex>
      )}
      extra={(
        <Button type="primary" icon={<SaveOutlined />} onClick={onSave} loading={saving}>
          Save
        </Button>
      )}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        This is a global rule shared by both inbound webhook handling and outbound sending.
      </Text>

      <Form form={form} layout="vertical" initialValues={{ enabled: false, match_mode: 'contains' }}>
        <Row gutter={ROW_GUTTER}>
          <Col span={12}>
            <Form.Item name="enabled" label="Enable restriction" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="match_mode" label="Match mode" rules={[{ required: true, message: 'Select match mode' }]}>
              <Select options={MATCH_MODE_OPTIONS} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="keywords_text"
          label="Restricted keywords (one per line)"
          rules={[{ required: true, message: 'Add at least one keyword' }]}
        >
          <Input.TextArea rows={4} placeholder={'abuse\nslur\nbanned phrase'} />
        </Form.Item>

        {previewKeywords.length > 0 && (
          <Form.Item label="Preview">
            <Space wrap>
              {previewKeywords.map((k) => (
                <Tag key={k}>{k}</Tag>
              ))}
            </Space>
          </Form.Item>
        )}

        <Row gutter={ROW_GUTTER}>
          <Col span={12}>
            <Form.Item
              name="store_as"
              label="Store-as message (Message 1)"
              extra="For inbound restricted message, this text is stored in DB instead of original abusive content."
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="reply_message"
              label="Reply/Replacement message (Message 2)"
              extra="Inbound: sent to customer as a WhatsApp auto-reply. Outbound (agent/tenant): shown as an error in the chat — nothing is sent to the end user."
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>

      </Form>
    </Card>
  );
};

export default KeywordRestriction;
